import type { ReactNode } from "react";

interface ButtonProps {
  variant?: "primary" | "ghost" | "inverse" | "ghost-light";
  /** Renders an anchor when present, otherwise a button. */
  href?: string;
  iconRight?: ReactNode;
  type?: "button" | "submit";
  className?: string;
  /** Ignored when `href` is set — a link CTA has no disabled state. */
  disabled?: boolean;
  children: ReactNode;
}

// Border color lives in each variant (exactly once) — pairing a base
// `border-transparent` with a variant border color left the winner to
// stylesheet order, which rendered ghost buttons borderless on white cards.
// disabled:pointer-events-none blocks every per-variant hover:* effect below
// (translate, background) without touching VARIANTS — hover can't trigger
// via pointer when the element ignores pointer events. Only reachable when a
// caller passes disabled={true}; the other 17 call sites never do.
const BASE =
  "inline-flex items-center gap-[9px] whitespace-nowrap rounded-[11px] border px-5 py-3 text-[15px] font-semibold transition-[transform,background-color,border-color] duration-150 disabled:cursor-not-allowed disabled:pointer-events-none";

const VARIANTS = {
  primary:
    "border-transparent bg-brand-600 text-white shadow-[0_10px_24px_-10px_rgba(215,14,98,0.55)] hover:-translate-y-px hover:bg-brand-500",
  ghost:
    "border-line-3 bg-surface text-ink hover:-translate-y-px hover:bg-surface-2 hover:border-ink/30",
  // For drenched (pink/dark) surfaces: white primary, glass secondary.
  inverse:
    "border-transparent bg-white text-brand-600 shadow-[0_10px_24px_-10px_rgba(0,0,0,0.4)] hover:-translate-y-px hover:bg-white/90",
  "ghost-light":
    "border-white/40 bg-transparent text-white hover:-translate-y-px hover:border-white/70 hover:bg-white/10",
} as const;

/** Primary / ghost call-to-action, rendered as `<a>` or `<button>`. */
export default function Button({
  variant = "primary",
  href,
  iconRight,
  type = "button",
  className = "",
  disabled = false,
  children,
}: ButtonProps) {
  const cls = `${BASE} ${VARIANTS[variant]} ${className}`;
  const inner = (
    <>
      {children}
      {iconRight}
    </>
  );

  if (href) {
    return (
      <a href={href} className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <button type={type} className={cls} disabled={disabled}>
      {inner}
    </button>
  );
}
